import { format } from "date-fns";
import { LogOut, Clock, Home } from 'lucide-react';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface SidebarFooterProps {
  isOpen: boolean;
  isMobile: boolean;
  profile: any;
  lastLogin: string | null;
  onLogout: () => Promise<void>;
}

export function SidebarFooter({ isOpen, isMobile, profile, lastLogin, onLogout }: SidebarFooterProps) {
  return (
    <div className="border-t p-4 space-y-4">
      {isMobile && (
        <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
          <span className="text-sm font-medium">Switch Theme</span>
          <ThemeToggle />
        </div>
      )}

      <div className="rounded-xl p-5">
        <div className={cn(
          "flex flex-col items-center space-y-4",
          !isOpen && !isMobile && "justify-center"
        )}>
          <Avatar className={cn(
            "border-2 border-primary/10 transition-all duration-300",
            isOpen ? "h-20 w-20" : "h-12 w-12"
          )}>
            <AvatarImage src={profile?.avatar_url} />
            <AvatarFallback className="bg-primary/5 text-primary font-medium text-xl">
              {profile?.full_name?.charAt(0) || profile?.email?.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          {(isOpen || isMobile) && (
            <div className="w-full text-center space-y-3">
              <div className="space-y-1.5">
                <h4 className="text-base font-semibold">
                  {profile?.full_name || 'User'}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {profile?.email}
                </p>
              </div>
              <div className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                Client ID: {profile?.client_number}
              </div>
              {lastLogin && (
                <div className="inline-flex items-center justify-center gap-1 text-[10px] text-muted-foreground bg-muted/50 rounded-full px-2 py-0.5 whitespace-nowrap">
                  <Clock className="h-2.5 w-2.5" />
                  <span>Last login: {format(new Date(lastLogin), "MMM d, h:mm a")}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start gap-2",
                  !isOpen && !isMobile && "justify-center px-2"
                )}
                onClick={() => window.location.href = '/'}
              >
                <Home className="h-4 w-4" />
                {(isOpen || isMobile) && <span>Go Home</span>}
              </Button>
            </TooltipTrigger>
            {!isOpen && !isMobile && (
              <TooltipContent side="right">
                Go Home
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onLogout}
                className={cn(
                  "flex items-center gap-2 w-full px-4 py-2.5 text-sm text-destructive hover:bg-destructive/10 rounded-lg transition-colors",
                  !isOpen && !isMobile && "justify-center px-2"
                )}
              >
                <LogOut className="h-4 w-4" />
                {(isOpen || isMobile) && <span>Sign Out</span>}
              </button>
            </TooltipTrigger>
            {!isOpen && !isMobile && (
              <TooltipContent side="right">
                Sign Out
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}